<template>
  <div
    class="hamburger"
    @click="visible = !visible"
  />

  <BfSideDrawer
    v-model:visible="visible"
    direction="left"
    :size="Sizes.sm"
    class="main-menu"
  >
    <template #header>
      <BfFlex
        justify="between"
        align="center"
      >
        {{ dealerName ?? t('dealer.fallback') }}

        <BfButton
          v-if="dealerName"
          @click="
            () => {
              userStore.logout()
              visible = false
              router.push({ name: 'start' })
            }
          "
        >
          {{ t('dealer.logout') }}
        </BfButton>
      </BfFlex>
    </template>

    <BfFlex direction="column">
      <BfCard :padding="Sizes.md">
        <div class="main-menu__header">
          <h3>{{ t('modules.index') }}</h3>
          <div class="main-menu__buttons">
            <BfButton
              v-if="!editMode"
              @click="showModal = true"
              :type="ButtonType.Outlined"
              >{{ t('common.change') }}</BfButton
            >
            <BfButton
              v-if="editMode"
              @click="cancel"
              :type="ButtonType.Outlined"
              >{{ t('common.cancel') }}</BfButton
            >
            <BfButton
              v-if="editMode"
              @click="save"
              >{{ t('common.save') }}</BfButton
            >
          </div>
        </div>

        <BfInputGroup>
          <BfCheckbox
            id="positioning"
            v-model="positioningMotors"
            name="positioning"
            :label="t('modules.positioning.index')"
            :disabled="!editMode"
          />
          <BfCheckbox
            id="xyTool"
            v-model="xyTool"
            name="xyTool"
            :disabled="!editMode"
            :label="t('modules.xyTool.index')"
          />
          <!-- BF-298
            <BfCheckbox
              id="motionAnalyzer"
              v-model="has3DMotionAnalyzer"
              name="motionAnalyzer"
              :label="t('modules.motionAnalyzer.index')"
            />
            <BfCheckbox
              id="pedalAnalyzer"
              v-model="has3DPedallingAnalyzer"
              name="pedalAnalyzer"
              :label="t('modules.pedalAnalyzer.index')"
            />
          -->
          <BfCheckbox
            id="heartrate"
            v-model="heartRateMonitor"
            name="heartrate"
            :disabled="!editMode"
            :label="t('modules.heartrate.index')"
          />
        </BfInputGroup>
      </BfCard>

      <BfCard :padding="Sizes.md">
        <h3>{{ t('app.language.index') }}</h3>
        <BfInputGroup>
          <BfRadio
            v-for="(lang, i) in availableLocales"
            :id="lang"
            :key="i"
            v-model="language"
            :name="lang"
            :value="lang"
            :label="t(`app.language.${lang}`)"
          />
        </BfInputGroup>
      </BfCard>

      <BfCard
        v-if="connected"
        :padding="Sizes.md"
      >
        <h3>{{ t('app.fitbike.index') }}</h3>
        <ControllerInfo />
      </BfCard>
    </BfFlex>

    <div class="main-menu__debug">
      <button
        v-if="!debug"
        class="main-menu__debug-button"
        @click="debug = true"
      >
        {{ t('app.debug.showDebugInfo') }}
      </button>
      <DebugInfo v-else />
    </div>
  </BfSideDrawer>

  <n-modal
    v-model:show="showModal"
    style="width: 600px"
    preset="card"
    :title="t('app.fitbike.settings.warning.title')"
    :bordered="false"
    size="huge"
    transform-origin="center"
  >
    <p>{{ t('app.fitbike.settings.warning.content') }}</p>

    <template #action>
      <div class="completion-view__actions">
        <BfButton
          :type="ButtonType.Outlined"
          @click="((showModal = false), (editMode = false))"
        >
          {{ t('common.cancel') }}
        </BfButton>
        <BfButton
          :state="ButtonState.Enabled"
          :type="ButtonType.Contained"
          @click="((showModal = false), (editMode = true))"
        >
          {{ t('common.confirm') }}
        </BfButton>
      </div>
    </template>
  </n-modal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  BfButton,
  BfCard,
  BfCheckbox,
  BfFlex,
  BfInputGroup,
  BfRadio,
  BfSideDrawer,
  Sizes,
  ButtonType,
  ButtonState,
} from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { usePreferenceStore } from '@/store/preferenceStore'
import { useMqttStore } from '@/store/mqttStore'
import { useUserStore } from '@/store/userStore'
import { NModal } from 'naive-ui'
import ControllerInfo from '@/components/controls/ControllerInfo.vue'
import DebugInfo from '@/components/controls/DebugInfo.vue'

const { t } = useI18n()
const i18n = useI18n()

const router = useRouter()

const preferenceStore = usePreferenceStore()
const mqttStore = useMqttStore()
const userStore = useUserStore()

const debug = ref(false)
const showModal = ref(false)
const editMode = ref(false)
const { connected } = storeToRefs(mqttStore)

defineProps<{
  dealerName?: string
}>()

const visible = ref(false)

const { hasPositioningMotors, hasXYTool, hasHeartRateMonitor } =
  storeToRefs(preferenceStore)

const language = ref(i18n.locale)

const save = () => {
  hasPositioningMotors.value = positioningMotors.value
  hasXYTool.value = xyTool.value
  hasHeartRateMonitor.value = heartRateMonitor.value

  userStore.logout()
  router.push({ name: 'start' })

  editMode.value = false
}

const cancel = () => {
  positioningMotors.value = hasPositioningMotors.value
  xyTool.value = hasXYTool.value
  heartRateMonitor.value = hasHeartRateMonitor.value

  editMode.value = false
}

const positioningMotors = ref(hasPositioningMotors.value)
const xyTool = ref(hasXYTool.value)
const heartRateMonitor = ref(hasHeartRateMonitor.value)

watch(language, (newLang) => {
  localStorage.setItem('ui-language', newLang)
})

/**
 * An array of all avialable locales is also avialable via `i18n.availableLocales`.
 * However, these are in lexical order. Client requests below order. This means that if languages
 * are added/removed in `i18n.ts`, they also have to be added/removed manually here.
 */
const availableLocales = [
  'zhs',
  'zht',
  'nl',
  'en',
  'fr',
  'de',
  'it',
  'ja',
  'pt',
  'ptb',
  'es',
]
</script>

<style lang="scss">
.hamburger {
  width: 6rem;
  height: 6rem;
  background-image:
    url('@/assets/menu-icon.svg'),
    linear-gradient(var(--color-primary), var(--color-secondary));
  background-repeat: no-repeat;
  background-size: 60%, cover;
  background-position: center;
}

.main-menu {
  .bf-dialog__header {
    & > div {
      width: 100%;
    }
  }

  &__debug {
    padding-top: 24px;
    display: flex;
    justify-content: center;
  }

  &__debug-button {
    background-color: transparent;
    color: var(--color-dark-20);
    font-size: 1rem;
    padding: 8px 12px;
    border-radius: 4px;

    &:hover {
      background-color: var(--color-dark-5);
      color: var(--color-dark-40);
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 1.6rem;

    h3 {
      margin: 0;
    }
  }

  &__buttons {
    display: flex;
    gap: 0.8rem;
  }
}
</style>
